/**
 * @file v1-request.ts
 *
 * Request helpers for the commerce-engine `/v1` API, layered on the shared
 * {@link apiFetch} wrapper (auth header, credentials, 401 handling). Unlike the
 * WooCommerce-style {@link shopGet} (bare body + `X-Total-*` headers), the `/v1`
 * API speaks the enveloped contract: a success is `{ data, meta }` and an error is
 * `{ error, meta }`, with list totals carried in `meta.pagination` (offset mode).
 * {@link v1Get} unwraps that envelope into the existing {@link WcResult} so the
 * stores/screens stay untouched, and throws {@link WcApiError} (carrying the
 * machine-readable `error.code`) on a non-2xx or error envelope. These coexist with
 * the `/shop` helpers in `request.ts` while domains are migrated one at a time.
 */

import { apiFetch } from '../../api/client';
import { WcApiError, type WcResult } from './types';

/** Base path for the versioned commerce-engine API. */
export const V1_API_BASE = '/v1';

/** The offset (page-number) pagination block carried on `/v1` list responses. */
interface OffsetPaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** The metadata block on every `/v1` response. */
interface ResponseMetaV1 {
  requestId?: string;
  timestamp?: string;
  pagination?: Partial<OffsetPaginationMeta>;
}

/** A `/v1` success envelope. */
interface SuccessEnvelopeV1<T> {
  data: T;
  meta: ResponseMetaV1;
}

/** A `/v1` error envelope. */
interface ErrorEnvelopeV1 {
  error: { code?: string; message?: string };
  meta?: ResponseMetaV1;
}

type EnvelopeV1<T> = SuccessEnvelopeV1<T> | ErrorEnvelopeV1;

function isErrorEnvelope<T>(body: EnvelopeV1<T>): body is ErrorEnvelopeV1 {
  return 'error' in body;
}

/**
 * Parses a `/v1` response, returning the success envelope or throwing on failure.
 *
 * @param response - The raw fetch {@link Response}.
 * @param path - The request path, for error messages.
 *
 * @returns The parsed success envelope.
 *
 * @throws {WcApiError} On a non-JSON body, a non-2xx status, or an error envelope.
 */
async function parseEnvelope<T>(
  response: Response,
  path: string,
): Promise<SuccessEnvelopeV1<T>> {
  let body: EnvelopeV1<T>;
  try {
    body = (await response.json()) as EnvelopeV1<T>;
  } catch {
    throw new WcApiError(
      `Request to ${path} returned a non-JSON response (${response.status})`,
      response.status,
      'invalid_response',
    );
  }
  if (!response.ok || isErrorEnvelope(body)) {
    const error = isErrorEnvelope(body) ? body.error : undefined;
    throw new WcApiError(
      error?.message ?? `Request to ${path} failed (${response.status})`,
      response.status,
      error?.code ?? '',
    );
  }
  return body;
}

/**
 * Performs a GET against a `/v1` endpoint and returns the unwrapped payload with
 * pagination totals read from `meta.pagination` (offset mode; `0` when absent).
 *
 * @param path - Path under {@link V1_API_BASE}, e.g. `"/products?page=1"`.
 *
 * @returns The decoded `data` payload wrapped in a {@link WcResult}.
 *
 * @throws {WcApiError} On a non-2xx status or an error envelope.
 */
export async function v1Get<T>(path: string): Promise<WcResult<T>> {
  const response = await apiFetch(`${V1_API_BASE}${path}`);
  const { data, meta } = await parseEnvelope<T>(response, path);
  return {
    data,
    total: meta.pagination?.total ?? 0,
    totalPages: meta.pagination?.totalPages ?? 0,
  };
}

/**
 * Performs a mutating `/v1` request (POST/PATCH/DELETE) and returns the unwrapped
 * `data` payload. A `204 No Content` (e.g. a soft delete) resolves to `undefined`.
 *
 * @param path - Path under {@link V1_API_BASE}, e.g. `"/products"`.
 * @param method - The HTTP method.
 * @param body - Optional JSON request body.
 *
 * @returns The decoded `data` payload (or `undefined` for a 204).
 *
 * @throws {WcApiError} On a non-2xx status or an error envelope.
 */
export async function v1Mutate<T>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const response = await apiFetch(`${V1_API_BASE}${path}`, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (response.ok && response.status === 204) {
    return undefined as T;
  }
  const { data } = await parseEnvelope<T>(response, path);
  return data;
}
